import * as React from "react";
import {useTheme} from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import {
  GridRowModes,
  DataGrid,
  GridActionsCellItem,
  GridRowEditStopReasons
} from "@mui/x-data-grid";
import {
  Card, 
  Chip, 
  FormControl, 
  InputLabel, 
  MenuItem, 
  Select,
  Box,
  IconButton,
  Tooltip,
  Typography,
  Paper
} from "@mui/material";
import {
  FirstPage,
  LastPage,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import {GET, POST} from "APIclient";
import {Snackbar, Alert} from "@mui/material";
import {useEffect, useState} from "react";
import properties from "constants/ApplicationConstants";

// Custom Pagination Component
function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading,
}) {
  const handleFirstPage = () => onPageChange(0);
  const handlePreviousPage = () => onPageChange(Math.max(0, page - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages - 1, page + 1));
  const handleLastPage = () => onGoToLastPage();

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="caption" sx={{ color: "#666" }}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No rows"}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" sx={{ mr: 2, color: "#666" }}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton
            onClick={handleFirstPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous Page">
          <IconButton
            onClick={handlePreviousPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor:
                  properties?.colors?.bgcolorbluethem || "#1565c0",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function ClientList() {
  const theme = useTheme();
  const [rowModesModel, setRowModesModel] = useState({});
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchParams, setSearchParams] = useState({
    clientName: "",
    clientCode: "",
    clientType: ""
  });
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [newId, setNewId] = useState(1);
  const [open, setOpen] = useState(false);
  const [snakColor, setSnakColor] = useState("");
  const [snakMessage, setSnakMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchData = async (page, pageSize, searchParams = {}, signal) => {
    try {
      setLoading(true);
      const params = {
        clientName: searchParams.clientName || null,
        clientCode: searchParams.clientCode || null,
        clientType: searchParams.clientType || null,
        page,
        size: pageSize
      };

      const queryString = Object.keys(params)
        .filter(key => params[key] !== null)
        .map(key => `${key}=${encodeURIComponent(params[key])}`)
        .join("&");

      const fetchUrl = `admin/client-list?${queryString}`;
      const response = await GET(fetchUrl, { signal });

      if (response.status === 200) {
        const data = response?.data?.data?.paginatedData || [];
        const totalRecords = response?.data?.data?.totalItems || 0;
        const totalPages = response?.data?.data?.totalPages || 0;

        const formattedData = data.map((item, index) => ({
          id: page * pageSize + index + 1,
          clientId: item?.clientId,
          clientName: item?.clientName,
          clientType: item?.clientType,
          clientCode: item?.clientCode,
          clientWebsite: item?.clientWebsite,
          contactNumber: item?.contactNumber,
          status: item?.status
        }));

        setData(formattedData);
        setFilteredData(formattedData);
        setTotalRows(totalRecords);
        setTotalPages(totalPages);

        if (data && data.length) {
          setNewId(data[data.length - 1].clientId + 1);
        } else {
          setNewId(1);
        }
      } else {
        setData([]);
        setFilteredData([]);
        setTotalRows(0);
        setTotalPages(0);
        setSnakColor("error");
        setSnakMessage("Failed to fetch data");
        setOpen(true);
      }
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Error fetching data:", error);
      setData([]);
      setFilteredData([]);
      setTotalRows(0);
      setTotalPages(0);
      setSnakColor("error");
      setSnakMessage("Error fetching data");
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchData(page, pageSize, searchParams, controller.signal);
    return () => controller.abort();
  }, [page, pageSize]);

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleEditClick = id => () => {
    setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.Edit}});
  };

  const handleSaveClick = id => () => {
    setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.View}});
  };

  const handleCancelClick = id => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: {mode: GridRowModes.View, ignoreModifications: true}
    });

    const editedRow = data.find(row => row.clientId === id);
    if (editedRow.isNew) {
      setData(data.filter(row => row.clientId !== id));
    }
  };

  const processRowUpdate = async newRow => {
    const updatedRow = {...newRow, isNew: false};
    const url = "admin/add-client";
    const dt = {
      clientId: updatedRow.clientId,
      clientName: updatedRow.clientName,
      clientType: updatedRow.clientType,
      clientWebsite: updatedRow.clientWebsite,
      contactNumber: updatedRow.contactNumber
    };

    try {
      const response = await POST(url, dt);
      if (response.status === 200) {
        setData(data.map(row => (row.clientId === updatedRow.clientId ? updatedRow : row)));
        setSnakColor("success");
        setSnakMessage("Client updated successfully");
        setOpen(true);
        await fetchData(page, pageSize, searchParams);
      }
    } catch (error) {
      console.error("Error updating client:", error);
      setSnakColor("error");
      setSnakMessage(error.response?.data?.message || "Error updating client");
      setOpen(true);
    }

    return updatedRow;
  };

  const handleProcessRowUpdateError = error => {
    setSnakColor("error");
    setSnakMessage(error.message);
    setOpen(true);
  };

  const handleRowModesModelChange = newRowModesModel => {
    setRowModesModel(newRowModesModel);
  };

  const onSearch = event => {
    const {name, value} = event.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = async () => {
    try {
      setPage(0);
      setLoading(true);
      await fetchData(0, pageSize, searchParams);
    } catch (error) {
      console.error("Error fetching search results:", error);
      setSnakColor("error");
      setSnakMessage("Error fetching search results");
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleGoToLastPage = async () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      setPage(lastPageIndex);
      await fetchData(lastPageIndex, pageSize, searchParams);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
    }
  };

  const renderStatusChip = params => {
    return (
      <Chip
        size="small"
        label={params.value == 1 ? "Active" : "Inactive"}
        color={params.value === 1 ? "success" : "error"}
        sx={{
          backgroundColor: params.value === 1 ? "green" : "red",
          color: "#fff",
          fontWeight: "400"
        }}
      />
    );
  };

  const columns = [
    {field: "id", headerName: "#", width: 20, headerAlign: "center", align: "center"},
    {
      field: "clientName",
      headerName: "Client Name",
      width: 120,
      headerAlign: "left",
      align: "left",
      editable: true,
      renderCell: params => params.value || "N/A"
    },
    {
      field: "clientType",
      headerName: "Client Type",
      width: 120,
      align: "left",
      headerAlign: "left",
      editable: true,
      type: "singleSelect",
      valueOptions: ["Ind", "Us"],
      renderCell: params => params.value || "N/A"
    },
    {
      field: "clientCode",
      headerName: "Client Code",
      width: 100,
      headerAlign: "left",
      align: "left"
    },
    {
      field: "clientWebsite",
      headerName: "Client Website",
      width: 200,
      headerAlign: "left",
      align: "left",
      editable: true
    },
    {
      field: "contactNumber",
      headerName: "Contact Number",
      width: 150,
      align: "left",
      headerAlign: "left",
      editable: true
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      headerAlign: "left",
      align: "left",
      renderCell: renderStatusChip
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({id}) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{color: "primary.main"}}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />
        ];
      }
    }
  ];

  const handleNullValues = data => {
    return data && data.length
      ? data.map(row => ({
          ...row,
          clientName: row.clientName ?? "N/A",
          clientType: row.clientType ?? "N/A",
          clientCode: row.clientCode ?? "N/A",
          clientWebsite: row.clientWebsite ?? "N/A",
          contactNumber: row.contactNumber ?? "N/A"
        }))
      : [];
  };

  const cleanedRows = handleNullValues(filteredData);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox container pt={2} pb={2} px={3}>
              
              <MDBox sx={{display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 3}}>
                <Typography
                  sx={{
                    gridColumn: "span 2",
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                 Client List
                </Typography>
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  value={searchParams.clientCode}
                  onChange={onSearch}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  name="clientCode"
                  label="Client Code"
                  variant="outlined"
                />
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  value={searchParams.clientName}
                  onChange={onSearch}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  name="clientName"
                  label="Client Name"
                  variant="outlined"
                />
                <FormControl variant="outlined" sx={{m: 0, width: "20ch", gridColumn: "span 2"}}>
                  <InputLabel id="clientType">Client Type</InputLabel>
                  <Select
                    labelId="clientType"
                    id="clientType"
                    name="clientType"
                    value={searchParams.clientType}
                    onChange={onSearch}
                    onKeyDown={event => {
                      if (event.key === "Enter") {
                        handleSearch();
                      }
                    }}
                    label="Client Type"
                    sx={{minWidth: 150, p: 1.5}}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    <MenuItem value="Ind">Ind</MenuItem>
                    <MenuItem value="Us">Us</MenuItem>
                  </Select>
                </FormControl>
                <MDButton
                  sx={{
                    gridColumn: "span 2",
                    fontFamily: properties.fontFamily,
                    background: properties.colors.bgcolorbluethem
                  }}
                  variant="contained"
                  color="info"
                  onClick={handleSearch}
                >
                  Search
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>

        {/* DataGrid with Custom Pagination */}
        <MDBox>
          <Paper>
            <DataGrid
              rows={cleanedRows}
              columns={columns}
              // rowHeight={35}
              editMode="row"
              rowModesModel={rowModesModel}
              getRowId={row => row.clientId}
              columnVisibilityModel={{
                clientId: false
              }}
              size="small"
              paginationMode="server"
              rowCount={totalRows}
              pageSizeOptions={[20]}
              hideFooter
              loading={loading}
              sx={{
                height: "450px",
                fontSize: "14px",
                fontWeight:400,
                fontFamily: properties.fontFamily,
                "& .MuiDataGrid-scrollbar": {
                  width: "10px !important",
                },
                "& .actions": {
                  color: "text.secondary"
                },
                "& .textPrimary": {
                  color: "text.primary"
                }
              }}
              aria-label="a dense table"
              onRowModesModelChange={handleRowModesModelChange}
              onRowEditStop={handleRowEditStop}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={handleProcessRowUpdateError}
              initialState={{
                sorting: {
                  sortModel: [{field: "clientId", sort: "asc"}]
                }
              }}
            />
            <CustomPagination
              page={page}
              pageSize={pageSize}
              totalRows={totalRows}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onGoToLastPage={handleGoToLastPage}
              loading={loading}
            />
          </Paper>
        </MDBox>

        <Snackbar
          open={open}
          anchorOrigin={{vertical: "top", horizontal: "right"}}
          autoHideDuration={6000}
          onClose={handleClose}
        >
          <Alert
            onClose={handleClose}
            severity={snakColor === "success" ? "success" : "error"}
            variant="filled"
            sx={{width: "100%", color: "#fff"}}
          >
            {snakMessage}
          </Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
}